import { useParams, Link } from "react-router-dom";
import { ArrowRight, Focus, Share2, Layers, CheckCircle } from "lucide-react";
import { Page } from "@/components/Reveal";
import SiteEnd from "@/components/SiteEnd";
import { services, rules } from "@/data/site";

const RULE_ICONS = { focus: Focus, share: Share2, layers: Layers };

export default function ServicePage() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return (
      <Page testId="service-not-found">
        <div className="site-container pt-40 pb-24 text-center">
          <h1 className="text-4xl font-semibold text-[#111]">Service not found</h1>
          <Link to="/#what-we-do" className="mt-8 inline-block text-[#8C5EBA] underline">
            See all services
          </Link>
        </div>
        <SiteEnd />
      </Page>
    );
  }

  const others = services.filter((s) => s.slug !== slug);

  return (
    <Page testId={`service-page-${slug}`}>
      {/* Hero — split: text left, image right */}
      <section className="site-container pt-24 sm:pt-32 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <h1 className="text-[48px] sm:text-[56px] lg:text-[64px] font-semibold leading-[1.08] tracking-[-0.04em] text-[#8C5EBA]">
              {service.name}.
            </h1>
            <p className="mt-5 text-lg sm:text-xl leading-relaxed text-[#111]/60 max-w-[480px]">
              {service.desc}
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="btn-pill btn-lav !py-3 !px-6 text-[15px] !font-semibold inline-flex"
              >
                Get Started
                <ArrowRight size={16} className="btn-arrow" />
              </Link>
            </div>
          </div>
          <div className="aspect-[4/3] overflow-hidden rounded-[24px]">
            <img
              src={service.heroImage}
              alt={service.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Deliverables — white box container, hww-card style cards with icon */}
      <section className="relative z-10 w-full px-6 pt-6 pb-4">
        <div className="bg-white rounded-[32px] overflow-hidden">
          <div className="site-container py-12 sm:py-16 text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#8C5EBA] mb-3">Deliverables</p>
            <h2 className="text-[28px] sm:text-[36px] font-semibold tracking-tight text-[#111] mb-10">
              What&apos;s Included.
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
              {service.deliverables.map((d) => (
                <div key={d.title} className="hww-card h-full p-6 transition-transform duration-500 hover:-translate-y-1.5">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(140,94,186,0.2)" }}>
                    <CheckCircle size={24} className="text-[#8C5EBA]" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-6 text-base sm:text-lg font-semibold text-[#111] tracking-tight">{d.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#111]/60">{d.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Rules — no container, hww-card style cards sit directly on bg-stripes */}
      <section className="relative z-10 w-full px-6 pt-6 pb-4">
        <div className="site-container py-12 sm:py-16">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#8C5EBA] mb-3">How we work</p>
          <h2 className="text-[28px] sm:text-[36px] font-semibold tracking-tight text-[#111] mb-10">
            Three rules we <span className="text-[#8C5EBA]">don&apos;t break.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {rules.map((r) => {
              const Icon = RULE_ICONS[r.icon];
              return (
                <div key={r.title[0]} className="hww-card h-full p-6 transition-transform duration-500 hover:-translate-y-1.5">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "rgba(140,94,186,0.2)" }}>
                    <Icon size={32} className="text-[#8C5EBA]" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-12 lg:mt-16 text-2xl lg:text-[30px] leading-snug tracking-tight">
                    <span className="font-semibold">{r.title[0]}</span>
                    <br />
                    <span className="font-normal">{r.title[1]}</span>
                  </h3>
                  <p className="mt-10 lg:mt-14 text-lg font-normal leading-relaxed text-[#111]/60 pb-2">{r.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Other services — #ECECEC outer + white inner, CAPABILITIES chip, centered 56px heading */}
      <section className="relative z-10 w-full px-6 pt-6 pb-12">
        <div className="bg-[#ECECEC] rounded-[40px] p-6">
          <div className="bg-white rounded-[32px] overflow-hidden">
            <div className="site-container py-24 text-center">
              <span
                className="inline-flex items-center rounded-full py-2.5 px-6 text-[12px] sm:text-[13px] font-semibold uppercase tracking-widest text-[#111]/80 mb-4"
                style={{ background: "rgba(236,236,236,0.3)", boxShadow: "0 4px 4px rgba(0,0,0,0.25)", backdropFilter: "blur(6px)" }}
              >
                Capabilities
              </span>
              <h2 className="text-[36px] sm:text-[48px] lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.14] text-[#111] max-w-[680px] mx-auto mb-16">
                Explore Our Other Services.
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 text-left">
                {others.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="group"
                  >
                    <div className="aspect-square overflow-hidden rounded-[16px]">
                      <img
                        src={s.image}
                        alt={s.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="pt-3">
                      <h3 className="text-sm font-semibold text-[#111] tracking-tight group-hover:text-[#8C5EBA] transition-colors leading-snug">{s.name}</h3>
                      <p className="mt-1 text-xs leading-relaxed text-[#111]/50 line-clamp-3">{s.desc}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteEnd />
    </Page>
  );
}
